import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Plus, Pencil, Trash2, LogOut, ExternalLink, Search, Flame } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/layout/Logo";
import { LeadsPanel } from "@/components/admin/LeadsPanel";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { RouteDialog } from "@/components/admin/RouteDialog";

const strip = ({ id, order, ...rest }) => rest;

export default function Admin() {
  const { user, logout } = useAuth();
  const [routes, setRoutes] = useState([]);
  const [q, setQ] = useState("");
  const [dialog, setDialog] = useState({ open: false, route: null });
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("routes");

  const load = () => api.get("/admin/routes").then((r) => setRoutes(Array.isArray(r.data) ? r.data : []))
    .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { if (user) load(); }, [user]);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return routes.filter((r) => `${r.origin_city} ${r.destination_city} ${r.origin_code} ${r.destination_code}`.toLowerCase().includes(s));
  }, [routes, q]);

  if (user === null) return <div className="min-h-screen bg-[#070B14] p-10 text-slate-300">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  const save = async (data) => {
    setSaving(true);
    try {
      if (dialog.route) await api.put(`/admin/routes/${dialog.route.id}`, data);
      else await api.post("/admin/routes", data);
      toast.success("Route saved");
      setDialog({ open: false, route: null });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggle = async (r, field = "active") => {
    try { await api.put(`/admin/routes/${r.id}`, { ...strip(r), [field]: !r[field] }); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete ${r.origin_code} → ${r.destination_code}?`)) return;
    try { await api.delete(`/admin/routes/${r.id}`); toast.success("Route deleted"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="dark min-h-screen bg-[#070B14] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#0A1120]">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-4 px-5 py-4">
          <Logo light testId="admin-logo" />
          <div className="flex items-center gap-3 text-sm">
            <Link to="/" target="_blank" data-testid="admin-view-site" className="flex items-center gap-1.5 text-slate-300 hover:text-white"><ExternalLink className="h-4 w-4" />View site</Link>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-slate-300 hover:text-white"><LogOut className="h-4 w-4" />Log out</button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10">
        <div className="mb-8 flex gap-2">
          {[["routes", "Flight routes"], ["leads", "Enquiries"]].map(([k, l]) => (
            <button key={k} onClick={() => setTab(k)} data-testid={`admin-tab-${k}`}
              className={`rounded-full px-4 py-2 text-sm font-bold ${tab === k ? "bg-amber-400 text-[#070B14]" : "border border-white/15 text-slate-300"}`}>{l}</button>
          ))}
        </div>
        {tab === "leads" ? <LeadsPanel /> : (<>
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
          <div>
            <h1 className="font-display text-3xl font-extrabold text-white">Flight routes</h1>
            <p className="mt-1 text-sm text-slate-400" data-testid="admin-route-count">{routes.length} routes · {routes.filter((r) => r.active).length} visible on site</p>
          </div>
          <button onClick={() => setDialog({ open: true, route: null })} data-testid="admin-add-route-btn"
            className="inline-flex items-center gap-2 rounded-full bg-amber-400 px-5 py-2.5 font-bold text-[#070B14]"><Plus className="h-4 w-4" />Add route</button>
        </div>

        <div className="relative mt-6 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search city or code" className="pl-9" data-testid="admin-search-input" />
        </div>

        <div className="mt-6 divide-y divide-white/10 overflow-hidden rounded-2xl border border-white/10 bg-[#0D1526]">
          {filtered.map((r) => (
            <div key={r.id} data-testid={`admin-route-row-${r.id}`} className="flex flex-wrap items-center gap-4 p-4">
              {r.image ? <img src={r.image} alt="" className="h-12 w-16 rounded-lg object-cover" /> : <div className="h-12 w-16 rounded-lg bg-white/5" />}
              <div className="min-w-[160px] flex-1">
                <p className="font-mono text-sm font-bold text-white">{r.origin_code} → {r.destination_code}</p>
                <p className="text-xs text-slate-400">{r.origin_city} to {r.destination_city} · {r.category}</p>
              </div>
              <button onClick={() => toggle(r, "hot")} data-testid={`admin-hot-${r.id}`} title={r.offer_ends ? `Offer ends ${r.offer_ends.replace("T", " ")}` : "No end date"}
                className={`flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-bold ${r.hot ? "bg-orange-500 text-white" : "border border-white/15 text-slate-400"}`}>
                <Flame className="h-3.5 w-3.5" />{r.hot ? "Hot" : "Not hot"}
              </button>
              <p className="w-16 font-display text-lg font-extrabold text-amber-400">${r.price}</p>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                <Switch checked={r.active} onCheckedChange={() => toggle(r)} data-testid={`admin-toggle-${r.id}`} /> {r.active ? "Live" : "Hidden"}
              </div>
              <div className="flex gap-1">
                <button onClick={() => setDialog({ open: true, route: r })} data-testid={`admin-edit-${r.id}`} className="rounded-lg p-2 text-slate-300 hover:bg-white/10" aria-label="Edit"><Pencil className="h-4 w-4" /></button>
                <button onClick={() => remove(r)} data-testid={`admin-delete-${r.id}`} className="rounded-lg p-2 text-red-400 hover:bg-red-500/10" aria-label="Delete"><Trash2 className="h-4 w-4" /></button>
              </div>
            </div>
          ))}
          {filtered.length === 0 && <p className="p-6 text-sm text-slate-400">No routes found.</p>}
        </div>
        </>)}
      </main>

      <RouteDialog open={dialog.open} initial={dialog.route ? strip(dialog.route) : null} saving={saving}
        onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))} onSave={save} />
    </div>
  );
}
